import { Search, X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { Button } from '@cenorion/ui/components/button';
import { Input } from '@cenorion/ui/components/input';

import { TOUR_TARGETS } from '@/shared/onboarding/tour-targets';

import { useGlobalSearchQuery } from '../../hooks/use-global-search-query';
import { DEVICES_PATH } from '../../services/search-adapter';
import { SEARCH_MIN_LENGTH } from '../../types';
import {
  SearchResultsPanel,
  searchPanelStopId,
  searchPanelStops,
} from '../search-results-panel/search-results-panel';

import type { GlobalSearchSlotProps } from '@/shared/layouts/global-search-slot';
import type { FormEvent, KeyboardEvent } from 'react';

/** How long the box sits quiet before the panel searches what was typed. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The header search: typing opens the grouped results under the box. Enter on a picked result
 * opens it; Enter with none picked still sends the text to the device registry as the identifier
 * filter. Focus never leaves
 * the input; the arrow keys move an active result (`aria-activedescendant`), the combobox pattern.
 */
export function GlobalSearch({ disabled }: GlobalSearchSlotProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reasonId = useId();
  const listboxId = useId();
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Read from the URL, not nuqs: this renders on every page, including ones that never
  // register the device param parsers.
  const activeIdentifier = searchParams.get('identifier') ?? '';

  const [query, setQuery] = useState(activeIdentifier);
  const [syncedIdentifier, setSyncedIdentifier] = useState(activeIdentifier);
  const [debounced, setDebounced] = useState(activeIdentifier);
  const [open, setOpen] = useState(false);
  const [activeKey, setActiveKey] = useState<string | null>(null);

  // Re-seeds the box when the URL filter changes underneath it. Done during render (the
  // supported "adjust state on prop change" form), not an effect, so the box never paints
  // one frame out of step with the table it describes.
  if (activeIdentifier !== syncedIdentifier) {
    setSyncedIdentifier(activeIdentifier);
    setQuery(activeIdentifier);
    setDebounced(activeIdentifier);
  }

  useEffect(() => {
    if (query === debounced) return;
    const timer = setTimeout(() => setDebounced(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, debounced]);

  const { groups, isLoading, isError, error, refetch } = useGlobalSearchQuery(debounced);
  const expanded = open && !disabled && debounced.trim().length >= SEARCH_MIN_LENGTH;
  const stops = expanded && !isLoading && !isError ? searchPanelStops(groups, debounced) : [];
  const keys = stops.map((stop) => stop.key);
  // A stop the latest answer dropped is no longer a stop.
  const active = activeKey !== null && keys.includes(activeKey) ? activeKey : null;

  const close = () => {
    setOpen(false);
    setActiveKey(null);
  };

  // Once a result opens here, the search is done: the box empties and the panel closes.
  const onOpen = () => {
    setQuery('');
    setDebounced('');
    close();
  };

  // A press anywhere outside the box and its panel closes the panel.
  useEffect(() => {
    if (!expanded) return;
    const onPointerDown = (event: PointerEvent) => {
      if (formRef.current?.contains(event.target as Node)) return;
      setOpen(false);
      setActiveKey(null);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [expanded]);

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      if (!expanded) return;
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    if (!open) {
      setOpen(true);
      return;
    }
    if (keys.length === 0) return;
    const step = event.key === 'ArrowDown' ? 1 : -1;
    const from = active === null ? (step === 1 ? -1 : keys.length) : keys.indexOf(active);
    setActiveKey(keys[(from + step + keys.length) % keys.length] ?? null);
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Enter on a picked result or "View all" opens it, as a click would.
    const picked = stops.find((stop) => stop.key === active);
    if (picked) {
      onOpen();
      void navigate(picked.href);
      return;
    }
    close();

    // An empty box with no filter in force has nothing to do; an empty box WITH a filter is
    // the way to clear it.
    if (!query && !activeIdentifier) return;

    // Built fresh so `page` cannot survive: page 3 of the previous filter rarely matches this one.
    const search = query ? `?${new URLSearchParams({ identifier: query }).toString()}` : '';
    void navigate(`${DEVICES_PATH}${search}`);
  };

  const onClear = () => {
    setQuery('');
    setDebounced('');
    close();
    inputRef.current?.focus();
  };

  return (
    <form
      ref={formRef}
      role="search"
      onSubmit={onSubmit}
      data-tour={TOUR_TARGETS.globalSearch}
      className="relative hidden sm:block"
    >
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
      />
      <Input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={expanded}
        aria-controls={expanded ? listboxId : undefined}
        aria-activedescendant={active === null ? undefined : searchPanelStopId(listboxId, active)}
        aria-autocomplete="list"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
          setActiveKey(null);
        }}
        onKeyDown={onKeyDown}
        placeholder={t('Search.Placeholder')}
        aria-label={t('Search.Placeholder')}
        // Identifier stays LTR under RTL. No `inputMode`: a serial carries letters, so a
        // numeric keypad would make one untypable on a touch device.
        dir="ltr"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        className="h-9 w-80 bg-card ps-8 pe-9 text-sm focus-visible:border-primary aria-expanded:border-primary md:text-sm"
        disabled={disabled}
        aria-describedby={disabled ? reasonId : undefined}
      />
      {query && !disabled ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          onClick={onClear}
          aria-label={t('Search.Clear')}
          className="absolute end-1.5 top-1/2 -translate-y-1/2"
        >
          <X aria-hidden="true" className="size-4" />
        </Button>
      ) : null}
      {disabled ? (
        <p id={reasonId} className="sr-only">
          {t('Search.Unavailable')}
        </p>
      ) : null}

      {expanded ? (
        <SearchResultsPanel
          listboxId={listboxId}
          query={debounced}
          groups={groups}
          status={isLoading ? 'loading' : isError ? 'error' : 'ready'}
          error={error}
          onRetry={() => void refetch()}
          activeKey={active}
          onOpen={onOpen}
          className="absolute inset-x-0 top-full z-50 mt-2"
        />
      ) : null}
    </form>
  );
}
